import { createSelector } from '@reduxjs/toolkit';
import { State } from 'app/reducers/index';
import * as fromMenuReducer from 'app/reducers/menu.reducer';
import {DisplayedGroup, GecoPosMenu, GroupStructure, Product, Size} from 'app/models/menu.model';

const selectSelf = (state: State): fromMenuReducer.MenuState => state[ fromMenuReducer.menuFeatureKey ];

export const selectRpPrograms = createSelector(
  selectSelf,
  (state) => {
    return state.dictionaryRpProgram;
  }
)

export const selectOriginalRpPrograms = createSelector(
  selectSelf,
  (state) => {
    return state.originalDictionaryRpProgram;
  }
)

export const selectSizeScales = createSelector(
  selectSelf,
  (state) => {
    return state.menu.sizeScales;
  }
)

export const selectAllergenGroups = createSelector(
  selectSelf,
  (state) => {
    return state.menu.allergenGroups;
  }
)

export const selectModifierGroupTypes = createSelector(
  selectSelf,
  (state) => {
    return state.menu.modifierGroupTypes;
  }
)

export const selectModifierGroupTypesDictionary = createSelector(
  selectSelf,
  (state) => {
    return state.dictionaryModifierGroupTypes;
  }
)

export const selectDictionaryAllergenGroup = createSelector(
  selectSelf,
  (state) => {
    return state.dictionaryAllergenGroup;
  }
)

export const selectOriginalDictionaryAllergenGroup = createSelector(
  selectSelf,
  (state) => {
    return state.originalDictionaryAllergenGroup;
  }
)

export const selectTags = createSelector(
  selectSelf,
  (state) => {
    return state.menu.tags;
  }
)

export const selectDictionaryTags = createSelector(
  selectSelf,
  (state) => {
    return state.dictionaryTags;
  }
)

export const selectOriginalDictionaryTags = createSelector(
  selectSelf,
  (state) => {
    return state.originalDictionaryTags;
  }
)

export const selectTagsWithDictionaryTags = createSelector(
  selectTags,
  selectDictionaryTags,
  (tags, dictionaryTags) => {
    return {tags, dictionaryTags};
  }
)

export const selectMenuOrganization = createSelector(
  selectSelf,
  (state) => {
    return state.organization;
  }
);

export const selectOverridePositionsMap = createSelector(
  selectSelf,
  (state) => {
    return state.overridePositionsMap;
  }
);

export const selectMenuSections = createSelector(
  selectSelf,
  (state) => {
    return state.menuSections;
  }
);

export const selectMenu = createSelector(
  selectSelf,
  (state) => {
    return state.menu;
  }
);

export const selectCurrency = createSelector(
  selectSelf,
  (state) => {
    return state.currency;
  }
);

export const selectMenuPending = createSelector(
  selectSelf,
  (state) => {
    return state.getMenusPending;
  }
);

export const selectDictionarySizes = createSelector(
  selectSelf,
  (state) => {
    return state.dictionarySizes;
  }
);

export const selectOriginalDictionarySizes = createSelector(
  selectSelf,
  (state) => {
    return state.originalDictionarySizes;
  }
);

export const selectDictionaryGroups = createSelector(
  selectSelf,
  (state) => {
    return state.dictionaryGroups;
  }
);

export const selectDictionaryGroupsAndOverridePositions = createSelector(
  selectSelf,
  (state) => {
    return {dictionaryGroups: state.dictionaryGroups, overridePositionsMap: state.overridePositionsMap};
  }
);

export const selectOriginalDictionaryGroups = createSelector(
  selectSelf,
  (state) => {
    return state.originalDictionaryGroups;
  }
);

export const selectDictionaryProducts = createSelector(
  selectSelf,
  (state) => {
    return state.dictionaryProducts;
  }
);

export const selectOriginalDictionaryProducts = createSelector(
  selectSelf,
  (state) => {
    return state.originalDictionaryProducts;
  }
);

export const selectFilteredDictionaryGroups = createSelector(
  selectSelf,
  (state) => {
    let result = new Map<string, DisplayedGroup>();

    for (let [key, group] of state.dictionaryGroups) {
      if (!state.searchQuery) {
        result.set(key, group);
        continue;
      } else if (group.name && group.name.toLowerCase().includes(state.searchQuery.toLowerCase())) {
        const filteredGroup = {...group, isFiltered: true};
        result.set(key, filteredGroup);
        continue;
      }

      let filteredGroup = {...group};
      filteredGroup.groupStructure = {
        productsInGroup: []
      } as GroupStructure;

      for (let productInGroup of group.groupStructure.productsInGroup) {
        const productInfo = state.dictionaryProducts.get(productInGroup.externalProductId);
        if (!filterProduct(productInfo, state.searchQuery)) {
          continue;
        }
        filteredGroup.groupStructure.productsInGroup.push(productInGroup);
      }

      if (!filteredGroup.groupStructure.productsInGroup.length) {
        continue;
      }

      result.set(key, filteredGroup);
    }

    return result;
  }
);

export const selectDictionaryModifierGroup = createSelector(
  selectSelf,
  (state) => {
    return state.dictionaryModifiersGroups;
  }
);

export const selectOriginalDictionaryModifierGroup = createSelector(
  selectSelf,
  (state) => {
    return state.originalDictionaryModifiersGroups;
  }
);

export const selectDictionaryComboGroup = createSelector(
  selectSelf,
  (state) => {
    return state.dictionaryComboGroup;
  }
);

export const selectOriginalDictionaryComboGroup = createSelector(
  selectSelf,
  (state) => {
    return state.originalDictionaryComboGroup;
  }
);

export const selectMenuSyncOfPOS = createSelector(
  selectSelf,
  (state) => {
    return state.menuSyncOfPOS;
  }
);

export const selectMenuSyncsOfSale = createSelector(
  selectSelf,
  (state) => {
    return state.menuSyncsOfSale;
  }
);

export const selectInfoForOverridePositions = createSelector(
  selectOverridePositionsMap,
  selectOriginalDictionaryGroups,
  selectOriginalDictionaryProducts,
  selectOriginalDictionarySizes,
  selectOriginalDictionaryModifierGroup,
  selectOriginalDictionaryComboGroup,
  (overridePositionsMap, originalDictionaryGroups, originalDictionaryProducts, originalDictionarySizes, originalDictionaryModifierGroup, originalDictionaryComboGroup) => {
    return {
      overridePositionsMap,
      originalDictionaryGroups,
      originalDictionaryProducts,
      originalDictionarySizes,
      originalDictionaryModifierGroup,
      originalDictionaryComboGroup
    };
  }
);

export const selectLoadingUploadingState = createSelector(
  selectSelf,
  (state) => {
    return (pipeId: string) => state.uploadsState.menuSyncLoadings[ pipeId ];
  }
);

export const selectErrorUploadingState = createSelector(
  selectSelf,
  (state) => {
    return (pipeId: string) => state.uploadsState.menuSyncErrors[ pipeId ];
  }
);

export const selectMenuSyncsOfSaleByMenu = createSelector(
  selectSelf,
  (state) => {
    return (menuId: string) => state.menuSyncsOfSale.filter(m => m.menuId === menuId);
  }
);

const filterProduct = (product: Product, query: string): boolean => {
  if (!product){
    return false;
  }
  return product.name && product.name.toLowerCase().includes(query.toLowerCase())
    || product.sku && product.sku.includes(query)
    || product.description && product.description.toLowerCase().includes(query.toLowerCase());
}

export const selectSearchQuery = createSelector(
  selectSelf,
  (state) => {
    return state.searchQuery;
  }
);

export const selectMenuBackup = createSelector(selectSelf,(state) => {
  return (backupId: string) => state.menuBackups.get(backupId);
})

export const selectMenuBackups = createSelector(selectSelf,(state) => {
  return state.menuBackups ?? new Map<string, GecoPosMenu>;
})

export const selectAllowOverrides = createSelector(selectSelf,(state) => {
  return state.allowOverrides;
})

export const selectForMenuMainContentComponent = createSelector(
  selectFilteredDictionaryGroups,
  selectOriginalDictionaryGroups,
  selectDictionaryProducts,
  selectOriginalDictionaryProducts,
  selectDictionaryModifierGroup,
  selectOriginalDictionaryModifierGroup,
  selectDictionaryComboGroup,
  selectOriginalDictionaryComboGroup,
  selectDictionarySizes,
  selectOriginalDictionarySizes,
  selectCurrency,
  selectSearchQuery,
  selectOverridePositionsMap,
  selectMenuSections,
  (groupMap,
   originalGroupMap,
   products,
   originalProducts,
   modifierGroups,
   originalModifierGroups,
   comboGroups,
   originalComboGroups,
   sizes,
   originalSizes,
   currency,
   searchQuery,
   overridePositionsMap,
   menuSections) => {
    return {
      groupMap,
      originalGroupMap,
      products,
      originalProducts,
      modifierGroups,
      originalModifierGroups,
      comboGroups,
      originalComboGroups,
      sizes,
      originalSizes,
      currency,
      searchQuery,
      overridePositionsMap,
      menuSections,
    };
  }
);

/** Значение productStructure.type у продукта-модификатора в сконвертированном меню GecoPos. */
const MODIFIER_PRODUCT_TYPE = 'Modifier';

/**
 * iiko-идентификатором является только externalId без подчёркивания: составные `guid_guid` —
 * внутренние идентификаторы GecoPos (в боевом меню их 1196 из 1394). В special convert
 * instructions они не фигурируют, поэтому в справочник попадать не должны.
 */
export const isIikoExternalId = (externalId: string): boolean => !!externalId && !externalId.includes('_');

/**
 * Плоский список модификаторов меню. Модификаторы лежат в products вместе с обычными товарами,
 * отличает их productStructure.type.
 *
 * Читаем menu напрямую, а не dictionaryProducts: словари наполняет MenuMainContentComponent,
 * и вне страницы /menu/main они пустые. menu при этом может быть null — отдаём пустой справочник.
 */
export const selectModifierProducts = createSelector(
  selectMenu,
  (menu): Product[] => (menu?.products ?? [])
    .filter(product => product.productStructure?.type === MODIFIER_PRODUCT_TYPE)
    .filter(product => isIikoExternalId(product.externalId))
);

/** Глобальный справочник размеров меню. */
export const selectMenuSizes = createSelector(
  selectMenu,
  (menu): Size[] => (menu?.sizes ?? []).filter(size => isIikoExternalId(size.externalId))
);

/** Один селектор на экран Special Instructions — одна подписка вместо двух. */
export const selectSpecialInstructionsDictionaries = createSelector(
  selectModifierProducts,
  selectMenuSizes,
  (modifiers, sizes) => ({modifiers, sizes})
);
